import { debounce } from 'lodash-es';
import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import type { ObservedSize } from 'use-resize-observer';
import useResizeObserver from 'use-resize-observer';

import type { TableState, Updater } from './useTable/types.js';

/**
 * Handles resizing of the DataTable root container.
 * @returns root ref
 * @internal
 */
export function useTableRootResize(
  /**
   * Remeasures the rows in the table.
   */
  remeasureRows: (virtualizationContainerWidth?: number) => void,
  /**
   * Sets the table state.
   */
  setTableState: (updater: Updater<TableState>) => void,
): MutableRefObject<HTMLDivElement | null> {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Keep track of the root container size. We are updating a reference to
  // the container size with the actual size.
  const rootContainerSize = useRef<number>(
    Math.round(rootRef.current?.getBoundingClientRect().width ?? 0),
  );
  //  Store debounced function in ref to cancel on unmount
  const debouncedResizeRef = useRef<ReturnType<typeof debounce>>();

  const onContainerResize = useCallback(
    (size: ObservedSize) => {
      if (size.width) {
        rootContainerSize.current = Math.round(size.width);
        // The `table.remeasureRow` is a memoized function, which will only run if
        // its parameters change.
        remeasureRows(rootContainerSize.current);
        // Trigger a re-render to recalculate styles that depend on container
        // dimensions, such as overflow behavior.
        setTableState((state) => ({ ...state }));
      }
    },
    [remeasureRows, setTableState],
  );

  useEffect(() => {
    // Debounce the resizing to avoid flickering and performance impacts
    // during resize. The rows are measured once the DataTable width stabilizes
    // for a given number of milliseconds.
    debouncedResizeRef.current = debounce(onContainerResize, 125);
    return () => debouncedResizeRef.current?.cancel();
  }, [onContainerResize]);

  // Track the resizing of the DataTable root container.
  useResizeObserver({
    ref: rootRef,
    onResize: (size) => debouncedResizeRef.current?.(size),
  });

  // When the table re-renders, invoke the measureRow with the last known
  // value for the containerSize in order to keep the memoization stable.
  remeasureRows(rootContainerSize.current);

  return rootRef;
}
